import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types'
import GeneralTab from "../account/GeneralTab";
import {Waypoint} from "react-waypoint";
import log from "../../../util/log";
import enums from "../../../util/enums";
import colors from "../../../util/colors";
import _ from 'lodash'
import MouseRectangleSelection from "../components/MouseRectangleSelection";
import PdfSignatureRequestOverlay, {
  calculateOverlayRelativeScale,
  MIN_OVERLAY_WIDTH,
  MIN_OVERLAY_HEIGHT,
  SIDE_BUTTON_WIDTH
} from "./PdfSignatureRequestOverlay";
import SignatureRequest from "../../../models/SignatureRequest";
import PdfSignatureFulfillOverlay from "./PdfSignatureFulfillOverlay";
import PdfPreview from "./PdfPreview";
import utils from "../../../util/util";
import modalActions from "../../../actions/modal-actions";
import pdfPreviewActions from "../../../actions/pdf-preview-actions";
import {connect} from "react-redux";
import ColorGenerator from "../../../helpers/color-generator";

class PdfPage extends Component {

  //This came from the pdf.js viewer.
  //https://mozilla.github.io/pdf.js/web/viewer.html
  static getOutputScale (ctx) {
    let devicePixelRatio = _.get(window, 'devicePixelRatio', 1);
    let backingStoreRatio =
      _.get(ctx, 'webkitBackingStorePixelRatio',
        _.get(ctx, 'mozBackingStorePixelRatio',
          _.get(ctx, 'msBackingStorePixelRatio',
            _.get(ctx, 'oBackingStorePixelRatio',
              _.get(ctx, 'backingStorePixelRatio',
                1)))));
    let pixelRatio = devicePixelRatio / backingStoreRatio;
    return {
      sx: pixelRatio,
      sy: pixelRatio,
      scaled: pixelRatio !== 1
    };
  }

  _renderTask = null;

  //The 500px is a little arbitrary.  I want the placeholder to be something large enough
  //So that they vertically tile for the Waypoint detection to more easily figure out what's going on.
  INITIAL_STATE = {
    canvasStyleHeight : '500px',
    canvasStyleWidth : '100%',
    canvasStyleWidthValue : 1,
    canvasStyleHeightValue : 1
  };

  constructor(props){
    super(props);

    this.placeholderDivRef = React.createRef();
    this.contextRef = React.createRef();
    this.canvasCallbackRef = this.onCanvasRef.bind(this);
    this.wrapperRef = React.createRef();
    this.state = _.extend({}, this.INITIAL_STATE);
  }

  componentDidMount() {
    if(this.props.onCanvasRef) {
      this.props.onCanvasRef(this.props.page.pageIndex, this)
    }
    this.updateCanvasSize();
  }

  componentWillUnmount(){
    if(this.props.onCanvasRef) {
      this.props.onCanvasRef(this.props.page.pageIndex, undefined);
    }
    this.clearCanvas();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if(!prevProps.isVisible && this.props.isVisible){
      this.renderPage();
    }
    else if (prevProps.isVisible && !this.props.isVisible) {
      this.clearCanvas();
    }
    else if(prevProps.scale !== this.props.scale){
      if(this.props.isVisible){
        this.renderPage();
      }
      else{
        this.updateCanvasSize();
      }
    }
    else if(prevProps.isDoingV1Signing !== this.props.isDoingV1Signing){
      this.renderPage();
    }
    else if(prevProps.allowSignatureRequestEditing !== this.props.allowSignatureRequestEditing){
      this.renderPage();
    }
  }

  onCanvasRef(el) {
    //mimic the react ref model so we can just drop this function in and use this.contextRef.current.
    //is this all they do anyway?
    this.contextRef = { current: el };
    if (el && this.props.isVisible) {
      //render right away if we're visible.
      this.renderPage();
    }
  }

  clearCanvas() {
    log.log('clearing canvas', this.props.page.pageIndex);
    //cancel any running renders.
    if (this._renderTask) {
      this._renderTask.cancel();
      this._renderTask = null;
    }

    //this is very important, clears memory.
    const canvas = this.contextRef.current;
    if (!canvas) return;
    canvas.width = 0;
    canvas.height = 0;
  }

  getPagePositionInScroll(){
    let node = this.wrapperRef.current;
    let rect = node.getBoundingClientRect();

    //log.log('page rect', this.props.page.pageIndex, rect);

    return {
      height : rect.height,
      width : rect.width,
      top : node.offsetTop,
      left : node.offsetLeft,
      right : node.offsetLeft + rect.width,
      bottom : node.offsetTop + rect.height
    }
  }

  getDivDimensions(){
    let node = this.placeholderDivRef.current;
    let rect = node.getBoundingClientRect();

    //log.log('div rect', this.props.page.pageIndex, rect);

    return {
      height : rect.height,
      width : rect.width,
      top : node.offsetTop,
      left : node.offsetLeft,
      right : node.offsetLeft + rect.width,
      bottom : node.offsetTop + rect.height
    }
  }

  getCanvasDimensions(){

    let { isVisible } = this.props;
    if(!isVisible){
      return this.getDivDimensions();
    }

    let node = this.contextRef.current;
    let rect = node.getBoundingClientRect();

    //log.log('page rect', this.props.page.pageIndex, rect);

    return {
      height : rect.height,
      width : rect.width,
      top : node.offsetTop,
      left : node.offsetLeft,
      right : node.offsetLeft + rect.width,
      bottom : node.offsetTop + rect.height
    }
  }

  updateCanvasSize(){
    //This calculates the size that the canvas will render to, based on a shared
    //hidden canvas that is always present in the modal window.
    //I do this so the scroll window size doesn't need to be recalculated as frequently.
    let { page, scale, sharedCanvasRef } = this.props;
    utils.waitForCondition(() => {
      return !!(_.get(sharedCanvasRef, 'current'));
    })
      .then(() => {
        let viewport = page.getViewport({scale: 1}).clone({ scale });
        let shareRef = _.get(sharedCanvasRef, 'current');
        if(!shareRef){
          return;
        }

        let ctx = null;
        try{
          ctx = shareRef.getContext('2d', {
            alpha: false
          });
        }
        catch(err){
          console.warn('error fetching shared canvas context', err);
        }

        if(!ctx){
          return;
        }

        this.setState({
          canvasStyleHeight : viewport.height + 'px',
          canvasStyleWidth : viewport.width + 'px',
          canvasStyleWidthValue : viewport.width,
          canvasStyleHeightValue : viewport.height
        })
      })
  }

  renderPage(){
    log.log('rendering page', _.get(this.props.page,'pageIndex'));

    //cancel if we're already going.
    if (this._renderTask) {
      this._renderTask.cancel();
      this._renderTask = null;
    }

    let contextRef = _.get(this, 'contextRef.current')
    if(!contextRef || !contextRef.getContext){
      log.warn('canvas does not exist', this.props.page.pageIndex, this.contextRef);
      this.updateCanvasSize();
      return;
    }

    let { page, scale } = this.props;
    let viewport = page.getViewport({scale: 1}).clone({ scale })

    let ctx = null;
    try{
      ctx = contextRef.getContext('2d', {
        alpha: false
      });
    }
    catch(err){
      console.warn('error fetching page canvas context', err);
    }

    if(!ctx){
      log.warn('canvas context does not exist', this.props.page.pageIndex, this.contextRef);
      this.updateCanvasSize();
      return;
    }

    let outputScale = PdfPage.getOutputScale(ctx);

    let canvasWidth  = viewport.width  * outputScale.sx | 0;
    let canvasHeight = viewport.height * outputScale.sy | 0;
    if (!canvasWidth || !canvasHeight || canvasWidth <= 0 || canvasHeight <= 0 || !isFinite(canvasWidth) || !isFinite(canvasHeight)) {
      log.warn('invalid canvas dimensions', this.props.page.pageIndex, { canvasWidth, canvasHeight, scale });
      this.updateCanvasSize();
      return;
    }

    contextRef.width  = canvasWidth;
    contextRef.height = canvasHeight;

    this.setState({
      canvasStyleHeight : viewport.height + 'px',
      canvasStyleWidth : viewport.width + 'px',
      canvasStyleWidthValue : viewport.width,
      canvasStyleHeightValue : viewport.height
    })

    let drawViewport = viewport.clone({scale});

    //Word to the wise...you CANNOT call ctx.scale() in here.
    //Doing this works SOMETIMES, but causes render problems on certain pdfs.  bug 1726
    //Instead, you're apparently supposed to use this undocumented method with this
    //transform property below.
    // I got that out of the pdf.js viewer code here - https://mozilla.github.io/pdf.js/web/viewer.html
    //I can't find any further documentation on this.
    let renderContext = {
      canvasContext: ctx,
      transform : !outputScale.scaled ? null : [outputScale.sx, 0, 0, outputScale.sy, 0, 0],
      viewport: drawViewport
    };

    this._renderTask = page.render(renderContext);
    return this._renderTask.promise
      .then(() => {
        // log.log('done rendering page', this.props.page.pageIndex);
      })
      .catch((err) => {
        log.log('error rendering page', this.props.page.pageIndex, err);
      })
      .finally(() => {
        this._renderTask = null;
      })
  }

  onMouseSelectionFinished(res){
    log.log('mouse selection finished', res);

    let { canvasStyleWidthValue } = this.state;

    let width = Math.abs(res.origin.x - res.target.x);
    let height = Math.abs(res.origin.y - res.target.y);

    //Special case for clicking on the page.
    //If you drew any box at all, we preserve the dimensions you entered.
    //Otherwise we want to intelligently place a scaled point where you clicked.
    if(width === 0 && height === 0){
      width = Math.max(MIN_OVERLAY_WIDTH, (canvasStyleWidthValue / this.props.scale) / 4)
    }

    if(width < MIN_OVERLAY_WIDTH){
      width = MIN_OVERLAY_WIDTH;
    }
    if(height < MIN_OVERLAY_HEIGHT){
      height = MIN_OVERLAY_HEIGHT;
    }

    let coords = {
      x : res.origin.x,
      y : res.origin.y
    }

    let editGuestInfo = this.props.pdfActions.getCurrentlyEditingGuestInfo();
    this.props.addSignatureRequest({
      id : _.uniqueId('vf-sig-request-overlay-'),
      pageIndex : this.props.page.pageIndex,
      coords,
      scale : this.props.scale,
      width,
      height,
      signatureType : SignatureRequest.SIGNATURE_REQUEST_TYPE.SIGNATURE,
      guest_uid : editGuestInfo.guest_uid,
      signaturePrimaryColor : ColorGenerator.generateColorFromId(editGuestInfo.guest_uid)
    });
  }

  onSignatureOverlayTypeChange(id, newType){

    let update = {
      id,
      signatureType : newType
    }

    //Make sure this gets set to an empty string, and not null, or react complains that
    //you're changing the input from controlled to uncontrolled.
    if(newType === SignatureRequest.SIGNATURE_REQUEST_TYPE.OTHER){
      update.signatureCustomLabel = '';
    }

    this.props.updateSignatureRequest(update)
  }

  onSignatureOverlayCustomLabelChange(id, val){
    this.props.updateSignatureRequest({
      id,
      signatureCustomLabel : val
    })
  }

  onSignatureOverlayMove(id, coords){

    this.props.updateSignatureRequest({
      id,
      coords
    })
  }

  onSignatureOverlayResize(id, dimensions){
    this.props.updateSignatureRequest({
      id,
      width : dimensions.width,
      height : dimensions.height,
    })
  }

  onDeleteSignature(id){
    this.props.deleteSignatureRequest(id);
  }

  onSignatureConfirmClick(id){
    this.props.signatureRequestOverlayConfirmed(id);
  }

  onSignatureMarkerSelect(id){
    this.props.signatureRequestOverlaySelected(id);
  }

  renderSignatureRequestOverlays(){
    let {
      canvasStyleWidthValue,
      canvasStyleHeightValue
    } = this.state;
    let {
      allowSignatureRequestEditing,
      scale,
      signatureRequestOverlays,
      isDoingV1Signing,
      isFulfillingSignatureRequest
    } = this.props;

    if(isFulfillingSignatureRequest || isDoingV1Signing){
      return null;
    }

    let editingGuestInfo = this.props.pdfActions.getCurrentlyEditingGuestInfo();

    return (
      <>
        {signatureRequestOverlays.map((overlay) => {

          //There is some tricky business with these overlay boundaries
          //We have to offset most of our calculations by SIDE_BUTTON_WIDTH so that the signature box appears where you drew it.
          //This also means we need to set it's draggable boundary wide enough to accommodate this same button, so you can
          //move a signature all the way to the edge of a pdf.
          //This also means that each overlay needs bounds calculated to it's scale, so that the offset is correct when you zoom.
          let leftPadding = calculateOverlayRelativeScale(SIDE_BUTTON_WIDTH, overlay.scale, scale);
          let rightPadding = calculateOverlayRelativeScale(SIDE_BUTTON_WIDTH, overlay.scale, scale);
          let totalPadding = leftPadding + rightPadding;

          return (
            <div className="position-absolute"
                 key={overlay.id}
                 style={{top: 0, bottom: 0, left: 0, right: 0, pointerEvents: 'none'}}>
              <div style={{
                position : 'relative',
                height: '100%',
                pointerEvents: 'none',
                marginLeft: `-${leftPadding}px`,
                width: `calc(${canvasStyleWidthValue}px + ${totalPadding}px)`
              }}>
                <PdfSignatureRequestOverlay
                  key={overlay.id}
                  signaturePrimaryColor={overlay.signaturePrimaryColor}
                  overlay={overlay}
                  viewScale={scale}
                  isSignatureForCurrentUser={(editingGuestInfo ? editingGuestInfo.guest_uid === overlay.guest_uid : false)}
                  scaledPageBounds={{width: canvasStyleWidthValue + leftPadding, height : canvasStyleHeightValue}}
                  allowChanges={allowSignatureRequestEditing}
                  isFulfillingSignatureRequest={isFulfillingSignatureRequest}
                  onSignatureCustomLabelChange={this.onSignatureOverlayCustomLabelChange.bind(this, overlay.id)}
                  onSignatureTypeChange={this.onSignatureOverlayTypeChange.bind(this, overlay.id)}
                  deleteSignature={this.onDeleteSignature.bind(this, overlay.id)}
                  onSignatureResize={this.onSignatureOverlayResize.bind(this, overlay.id)}
                  onSignatureMove={this.onSignatureOverlayMove.bind(this, overlay.id)} />
              </div>
            </div>
          )
        })}
      </>
    )
  }

  renderSignatureFulfillOverlays(){
    let {
      canvasStyleWidthValue,
      canvasStyleHeightValue
    } = this.state;
    let {
      allowSignatureRequestEditing,
      scale,
      signatureRequestOverlays,
      isFulfillingSignatureRequest
    } = this.props;
    let {
      accountInfoGuest
    } = this.props.sharedState;

    if(!isFulfillingSignatureRequest){
      return null;
    }

    return (
      <>
        {signatureRequestOverlays.map((overlay) => {
          let isYou = accountInfoGuest && accountInfoGuest.guest_uid === overlay.guest_uid;
          let leftPadding = calculateOverlayRelativeScale(SIDE_BUTTON_WIDTH, overlay.scale, scale);
          let rightPadding = calculateOverlayRelativeScale(SIDE_BUTTON_WIDTH, overlay.scale, scale);
          let totalPadding = leftPadding + rightPadding;

          return (
            <div className="position-absolute"
                 key={overlay.id}
                 style={{top: 0, bottom: 0, left: 0, right: 0, pointerEvents: 'none'}}>
              <div style={{
                position : 'relative',
                height: '100%',
                pointerEvents: 'none',
                marginLeft: `-${leftPadding}px`,
                width: `calc(${canvasStyleWidthValue}px + ${totalPadding}px)`
              }}>
                <PdfSignatureFulfillOverlay
                  key={overlay.id}
                  signaturePrimaryColor={isYou ? colors.PRIMARY : overlay.signaturePrimaryColor}
                  allowInteraction={isYou}
                  overlay={overlay}
                  viewScale={scale}
                  onRef={(id, ref) => this.props.onFulfillSignatureOverlayRef(id, ref)}
                  onMarkerSelect={this.onSignatureMarkerSelect.bind(this, overlay.id)}
                  onCustomTextChange={(evt) => this.props.signatureRequestOverlayCustomTextChanged(overlay.id, evt)}
                  onConfirmClick={this.onSignatureConfirmClick.bind(this, overlay.id)} />
              </div>
            </div>
          )
        })}
      </>
    )
  }

  render() {
    let {
      canvasHeight,
      canvasWidth,
      canvasStyleHeight,
      canvasStyleWidth,
    } = this.state;

    let {
      isVisible,
      page,
      allowSignatureRequestEditing,
      isDoingV1Signing
    } = this.props;

    let editingGuestInfo = this.props.pdfActions.getCurrentlyEditingGuestInfo();

    return (
      <div ref={this.wrapperRef}
           key={page.pageIndex}
           style={PdfPage.styles.pageWrap}>
        <div className="text-center" style={
          isDoingV1Signing ?
            {display: 'contents'} :
            (isVisible ? {
              position : 'relative',
              marginTop : PdfPage.CANVAS_MARGIN + 'px',
              marginBottom : PdfPage.CANVAS_MARGIN + 'px',
            } : {position : 'relative'})
        }>
          {this.renderSignatureRequestOverlays()}
          {this.renderSignatureFulfillOverlays()}

          <MouseRectangleSelection disabled={!allowSignatureRequestEditing || !isVisible}
                                   {...(allowSignatureRequestEditing && editingGuestInfo &&
                                     {
                                       signaturePrimaryColor: ColorGenerator.generateColorFromId(editingGuestInfo.guest_uid)
                                     }
                                   )}
                                   onSelectFinished={this.onMouseSelectionFinished.bind(this)}>
            <canvas ref={this.canvasCallbackRef}
                    className={`pdf-page-canvas pdf-canvas-${page.pageIndex}`}
                    style={{
                      display: isVisible ? 'block' : 'none',
                      ...(isDoingV1Signing ? PdfPage.styles.canvas : null),
                      ...(allowSignatureRequestEditing ? PdfPage.styles.canvasCrosshair : null),
                      ...{
                        width: canvasStyleWidth,
                        height: canvasStyleHeight
                      }
                    }}/>
          </MouseRectangleSelection>
        </div>

        {!isVisible &&
        <div ref={this.placeholderDivRef}
             style={{...PdfPage.styles.canvasPlaceholder, width: canvasStyleWidth, height: canvasStyleHeight}}
             className="d-inline-block light-bg"/>
        }
      </div>
    )
  }
}

PdfPage.CANVAS_MARGIN = 20;
PdfPage.styles = {
  pageWrap : {
    textAlign: 'center',
    backgroundColor : colors.TRANSPARENT,
    display: 'flex',
    justifyContent : 'center'
  },
  canvas : {
    //extra horizontal room so v1 signing overlays can be dragged past the
    //page's right edge in all cases (like when zoomed in).
    margin: PdfPage.CANVAS_MARGIN + 'px ' + (PdfPage.CANVAS_MARGIN * 3) + 'px',
  },
  canvasWrap : {
    position : 'relative',
    margin : PdfPage.CANVAS_MARGIN + 'px',
  },
  canvasPlaceholder : {
    margin : PdfPage.CANVAS_MARGIN + 'px'
  },
  canvasCrosshair : {
    cursor : 'crosshair'
  }
}

PdfPage.propTypes = {
  pdf : PropTypes.object.isRequired,
  page : PropTypes.object.isRequired,
  scale : PropTypes.number.isRequired,
  isVisible : PropTypes.bool.isRequired,
  sharedCanvasRef : PropTypes.object.isRequired,
  onCanvasRef : PropTypes.func.isRequired,

  isFulfillingSignatureRequest : PropTypes.bool.isRequired,
  allowSignatureRequestEditing : PropTypes.bool.isRequired,
  addSignatureRequest : PropTypes.func.isRequired,
  updateSignatureRequest : PropTypes.func.isRequired,
  deleteSignatureRequest :PropTypes.func.isRequired,
  signatureRequestOverlays : PropTypes.array.isRequired,
  signatureRequestOverlaySelected : PropTypes.func.isRequired,
  signatureRequestOverlayConfirmed : PropTypes.func.isRequired,
  signatureRequestOverlayCustomTextChanged : PropTypes.func.isRequired,
  onFulfillSignatureOverlayRef : PropTypes.func.isRequired,

  isDoingV1Signing : PropTypes.bool.isRequired,
}

const mapStateToProps = (state) => {
  return {
    sharedState : {...state.shared},
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    pdfActions : {...pdfPreviewActions.mapToDispatch(dispatch)},
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PdfPage);
